// The Curator API as the Exchange serves it: the Curator API contract's
// endpoints (src/contract/curator-api.ts) with the Exchange's own Bearer
// middleware, which provides the CurrentCaller, and the summaries and
// activation checks the handlers build from D1 rows and config. The handlers
// are in src/exchange/curator-api-handlers.ts. See Design > Exchange,
// "Curator API".

import { Context, Effect, type Redacted } from "effect";
import { HttpApiMiddleware, HttpApiSecurity } from "effect/http-api";
import { BearerFailures, curatorApiOf, curatorEndpoints, type TestRunSummary, type ToolsetSummary } from "../contract/curator-api.ts";
import { SUPPORTED_SDK_VERSIONS, TEST_RUN_TIMEOUT_MS } from "../config.ts";
import type { Blocker, HubId, HubSecretState } from "../domain/types.ts";
import type { NotOperatorOrgMember, WarmHubError } from "../domain/errors.ts";
import type { GoldenComparisonCounts } from "./test-runs.ts";
import type { HubRecord, Toolset, ToolsetStatus } from "../hubs/toolsets.ts";
import type { ExchangeServices } from "../layers.ts";
import type { CuratorView, HubRef } from "./curators.ts";
import type { TestRunRow } from "../services/hub-store.ts";

// The contract's schemas and IDs, for the Exchange's modules and the
// clients that import them from here. See src/contract/curator-api.ts.
export {
  ActivationSummary, CommitSha, DemandResponse, EventsResponse, GoldenResponse, HubId, HubsResponse, HubSummary,
  ServiceUnavailableBody, TestRunId, TestRunKind, TestRunSummary, ToolsetSummary, curatorOpenApi,
  type Blocker, type HubSecretState, type HubSecretsSummary, type Json, type Listing,
} from "../contract/curator-api.ts";
// The hub agent's activity, which the Curator API's agent routes serve, and
// its Interactions. See src/contract/agent-activity.ts and interaction.ts.
export {
  ACTIVITY_LIMIT, ActionId, AgentActionTranscript, AgentActivityResponse, HUB_AGENT_TOOL_NAMES, MarkInteractionsRead, WaitingInteractionsResponse,
  type AgentActionView, type InteractionView, type RequestRepairResponse, type TranscriptItem, type WaitingInteraction,
} from "../contract/agent-activity.ts";
export {
  CuratorReply, FIXED_OPTIONS, INTERACTION_STATES, InteractionId, PURPOSES, checkReply,
  type ChoiceOption, type DraftQuestion, type InteractionState, type PriceSuggestion, type Purpose, type ReplySchema, type Subject, type SubjectKind,
} from "../contract/interaction.ts";
export { HUB_EVENT_KINDS } from "../contract/hub-events.ts";

// A test run as its readers see it: one still running after
// TEST_RUN_TIMEOUT_MS reads as an error, since its Workflow has stopped.
export const runAsOf = <R extends TestRunRow>(r: R, now: number): R =>
  r.status === "running" && now - r.started_at > TEST_RUN_TIMEOUT_MS ? { ...r, status: "error" } : r;

// The latest run of `kind` of the hub's toolset at `commit`, from runs newest
// first, as its readers see it.
export const latestRun = (runs: readonly TestRunRow[], hubId: string, commit: string, kind: TestRunRow["kind"], now: number) => {
  const r = runs.find((x) => x.hub_id === hubId && x.commit_sha === commit && x.kind === kind);
  return r && runAsOf(r, now);
};

export const runSummary = (r: TestRunRow): TestRunSummary => ({
  id: r.run_id, kind: r.kind, status: r.status, passed: r.passed, total: r.total, startedAt: r.started_at, finishedAt: r.finished_at,
});

// The readable repositories the hub's WarmHub token doesn't cover, and the
// part of them that a grant can't fix. See the HubSecretMissing blocker.
export type Uncovered = { readonly missing: readonly string[]; readonly unreadable: readonly string[] };
export const COVERED: Uncovered = { missing: [], unreadable: [] };

// What a HubSecretMissing blocker or refusal tells a curator: who acts, and on
// which repositories. While hubs read with the platform's shared WarmHub
// token, a repository it can't read is one the hub can't read either, grant
// or none: make it public, or the operator adds it to that token. See Design >
// Operations, "Hubs and public repositories".
export function coverageDetail({ missing, unreadable }: Uncovered): string {
  const unfixable = missing.filter((r) => unreadable.includes(r));
  const grantable = missing.filter((r) => !unreadable.includes(r));
  const parts = [
    ...(grantable.length > 0 ? [`A curator of the hub grants this toolset access to ${grantable.join(", ")}.`] : []),
    ...(unfixable.length > 0
      ? [`The platform's shared WarmHub token can't read ${unfixable.join(", ")}, so a grant can't cover ${unfixable.length === 1 ? "it" : "them"}: make ${unfixable.length === 1 ? "it" : "them"} public, or ask the operator to add ${unfixable.length === 1 ? "it" : "them"} to that token, and then a grant covers ${unfixable.length === 1 ? "it" : "them"}.`]
      : []),
  ];
  return parts.join(" ");
}

// The activation checks a toolset hasn't met, from its row, its hub record,
// its test runs (newest first) and the readable repositories its hub's
// WarmHub token doesn't cover. Pure, so each check is tested without D1.
// Staging rejects a Tool SDK version not in SUPPORTED_SDK_VERSIONS, so
// ToolSdkUnsupported is for a version dropped since (#43). The runs come through runAsOf, so one past its budget reads as an error.
// See Design > Names and definitions, "Blocker".
export function blockersOf(hub: HubRecord, toolset: { status: ToolsetStatus; sdkVersion: string | null; activatedAt: number | null }, runs: readonly TestRunRow[], uncovered: Uncovered): Blocker[] {
  const blockers: Blocker[] = [];
  if (hub.listing === "archived") blockers.push({ _tag: "HubArchived", detail: "The hub is archived. Register it again to activate a toolset." });
  if (toolset.status === "validating" || toolset.status === "rejected") {
    blockers.push({ _tag: "NotStaged", status: toolset.status, detail: toolset.status === "validating" ? "The toolset is still being staged." : "The toolset was rejected at staging." });
  }
  if (toolset.sdkVersion !== null && !SUPPORTED_SDK_VERSIONS.includes(toolset.sdkVersion)) {
    blockers.push({ _tag: "ToolSdkUnsupported", sdkVersion: toolset.sdkVersion, supported: [...SUPPORTED_SDK_VERSIONS],
      detail: `The toolset was built with Tool SDK ${toolset.sdkVersion}, which is no longer supported. Rebuild it with ${SUPPORTED_SDK_VERSIONS.join(" or ")}, push, and stage it.` });
  }
  if (uncovered.missing.length > 0) {
    blockers.push({ _tag: "HubSecretMissing", missing: [...uncovered.missing], unreadable: [...uncovered.unreadable], detail: coverageDetail(uncovered) });
  }
  // A toolset that was active has served consumers, so a rollback to it needs
  // only its grant, whatever runs it has (Jeff, 2026-10-14).
  if (toolset.activatedAt !== null) return blockers;
  const examples = runs.find((r) => r.kind === "examples");
  if (!examples) blockers.push({ _tag: "ExamplesNotRun", detail: "Run the examples." });
  else if (examples.status === "running") blockers.push({ _tag: "ExamplesRunning", runId: examples.run_id, detail: "The examples are running." });
  else if (examples.status !== "passed") blockers.push({ _tag: "ExamplesFailed", runId: examples.run_id, detail: "The latest examples run didn't pass." });
  // A golden run counts once it ends passed or failed: one that ended in
  // error may have asked nothing.
  const golden = runs.filter((r) => r.kind === "golden");
  if (!golden.some((r) => r.status === "passed" || r.status === "failed")) {
    const running = golden.find((r) => r.status === "running");
    blockers.push(running
      ? { _tag: "GoldenRunning", runId: running.run_id, detail: "The golden questions are running." }
      : { _tag: "GoldenNotRun", detail: golden.length > 0 ? "The golden run ended in error. Run the golden questions again." : "Run the golden questions." });
  }
  return blockers;
}

// A toolset's summary, from the readable repositories its hub's WarmHub token
// doesn't cover (uncoveredOf, in staging-api.ts), its runs (any commit's;
// only its own count) and the comparison counts by run ID (comparisonCounts,
// in golden-comparison.ts; none for an active toolset). The active toolset has no blockers: a rollback activates
// another, staged toolset, so a blocker on the live one would name nothing a
// curator can do (Jeff, 2026-10-14).
export function toolsetSummaryOf({ hub, toolset }: { hub: HubRecord; toolset: Toolset }, { uncovered, secret }: { uncovered: Uncovered; secret: HubSecretState }, runs: readonly TestRunRow[], comparisons: ReadonlyMap<string, GoldenComparisonCounts> = new Map(), now = Date.now()): ToolsetSummary {
  const own = runs.filter((r) => r.hub_id === hub.id && r.commit_sha === toolset.commit).map((r) => runAsOf(r, now));
  const latest = (kind: TestRunRow["kind"]) => {
    const r = latestRun(runs, hub.id, toolset.commit, kind, now);
    return r ? runSummary(r) : null;
  };
  const active = toolset.status === "active";
  const blockers = active ? [] : blockersOf(hub, toolset, own, uncovered);
  const golden = latest("golden");
  return {
    commit: toolset.commit, status: toolset.status, problems: toolset.problems, sdkVersion: toolset.sdkVersion, stagedBy: toolset.stagedBy,
    createdAt: toolset.createdAt, activatedAt: toolset.activatedAt, bundles: Object.keys(toolset.tools).length,
    runs: { examples: latest("examples"), golden },
    canActivate: !active && blockers.length === 0,
    blockers,
    comparison: !active && golden ? comparisons.get(golden.id) ?? null : null,
    secret,
  };
}

// The caller of a Curator API request, once the Bearer middleware has
// checked its bearer token, its rate limit and the operator org: its WarmHub
// user and token, and its roles on the hubs a handler asks about, from the
// cache or fresh, as a write asks. See Design > Exchange, "Curator API".
// `agent` is set only on the agent path: the hub agent of `hubId`, acting
// with the Hub token of `onBehalfOf`, the curator who connected it, who
// is also `userId`; `bearer` is then that token. Null for every other caller.
// See Design > Exchange, "The agent path".
export class CurrentCaller extends Context.Service<CurrentCaller, {
  readonly userId: string;
  readonly bearer: Redacted.Redacted;
  readonly roles: (hubs: readonly HubRef[], fresh: boolean) => Effect.Effect<CuratorView, NotOperatorOrgMember | WarmHubError>;
  readonly agent: { readonly hubId: HubId; readonly onBehalfOf: string } | null;
}>()("answer-exchange/exchange/CurrentCaller") {}

// Around every route, a schema error's answer: a request that doesn't decode
// is the caller's, Effect's own empty 400; a response that doesn't encode is
// a defect. Its layer is Effect's layerSchemaErrorTransform, in
// src/exchange/curator-api-handlers.ts. See Design > Errors, "A request that
// doesn't decode is a 400".
export class SchemaErrors extends HttpApiMiddleware.Service<SchemaErrors>()("answer-exchange/exchange/SchemaErrors") {}

// Every route needs a WarmHub bearer token. No environment serves the Curator
// API anonymously. The middleware makes the checks every route shares, the
// token, the rate limit and the operator org, before the route's parameters
// and body are decoded, so a request without a token learns nothing from
// either; each handler asks the CurrentCaller for the roles it needs. It provides the request's services and the
// CurrentCaller. See Design > Exchange, "Bearer tokens".
export class Bearer extends HttpApiMiddleware.Service<Bearer, { provides: CurrentCaller | ExchangeServices }>()("answer-exchange/exchange/Bearer", {
  security: { bearer: HttpApiSecurity.bearer },
  error: BearerFailures,
}) {}

// The Curator API as the Exchange serves it: the contract's endpoints, with
// SchemaErrors and the Bearer middleware around every route.
export const CuratorApi = curatorApiOf(curatorEndpoints.middleware(SchemaErrors).middleware(Bearer));
